// Lib
import { useState } from 'react';
import { useSelector } from 'react-redux';

// Selectors
import {
    getAttachmentForElement,
    getFetching,
    getLoaded,
    getTotal,
} from '../../../../../attachments/attachmentsSelector';
import { getBackgroundRemovalAttachmentId } from '../../../../../attachments/attachmentIds';

// Types
import type { ReduxState } from '../../../../../../types/reduxTypes';
import {
    BACKGROUND_REMOVAL_PROGRESS_TYPE,
    type BackgroundRemovalProgress,
} from '../../../backgroundRemoval/imageModalBackgroundRemovalProgressTypes';

/**
 * Uses the final processed-image upload progress when it is available.
 */
const getUploadProgress = (
    isUploading?: boolean,
    loaded?: number,
    total?: number,
): BackgroundRemovalProgress | null => {
    if (!isUploading) return null;
    if (!total) {
        return {
            type: BACKGROUND_REMOVAL_PROGRESS_TYPE.INDETERMINATE,
        };
    }

    return {
        type: BACKGROUND_REMOVAL_PROGRESS_TYPE.DETERMINATE,
        percentageComplete: Math.min(((loaded || 0) / total) * 100, 100),
    };
};

/**
 * Tracks remove-background progress, preserving the combined progress value used by v1 callers.
 */
export const useImageModalBackgroundRemovalProgress = (elementId: string) => {
    const [backgroundRemovalProgress, setBackgroundRemovalProgress] = useState<BackgroundRemovalProgress | null>(null);

    const backgroundRemovalAttachment = useSelector((state: ReduxState) =>
        getAttachmentForElement(state, getBackgroundRemovalAttachmentId(elementId)),
    );

    const uploadProgress = getUploadProgress(
        getFetching(backgroundRemovalAttachment),
        getLoaded(backgroundRemovalAttachment),
        getTotal(backgroundRemovalAttachment),
    );
    const visibleBackgroundRemovalProgress = uploadProgress || backgroundRemovalProgress;

    return {
        backgroundRemovalProgress: visibleBackgroundRemovalProgress,
        backgroundRemovalApiProgress: backgroundRemovalProgress,
        backgroundRemovalUploadProgress: uploadProgress,
        setBackgroundRemovalProgress,
        isRemovingBackground: !!visibleBackgroundRemovalProgress,
    };
};
